import { Link } from 'react-router-dom';
import { useMyPlatforms, useHandoff } from '../hooks/use-platforms';
import type { MyPlatform } from '../../domain/entities/platform.entity';

/**
 * Uma pessoa com acesso a mais de uma plataforma não tinha como saber disso pela
 * própria plataforma. Esta tela é a lista, e cada card leva ao destino **já
 * logado** (handoff SSO) ou aos acessos daquele perfil.
 *
 * A lista já vem filtrada pelo back: plataforma sem endereço configurado não
 * aparece (botão que não leva a lugar nenhum é defeito, não degradação), e a
 * plataforma atual também não.
 */
export function PlatformsPage() {
  const { data: platforms, isLoading, isError } = useMyPlatforms();
  const { goToPlatform, pendingScopeId, error } = useHandoff();

  return (
    <div className="max-w-240 mx-auto space-y-6">
      <div>
        <h1 className="text-(--color-text-primary) text-2xl font-bold">
          Minhas plataformas
        </h1>
        <p className="text-(--color-text-secondary) mt-1 text-sm">
          Acesse as plataformas em que você tem cadastro, sem entrar de novo
        </p>
      </div>

      {error && (
        <p
          role="alert"
          className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600 dark:bg-red-950/50 dark:text-red-400"
        >
          {error}
        </p>
      )}

      {isLoading && <PlatformsSkeleton />}

      {isError && (
        <p className="text-(--color-text-secondary) text-sm">
          Não foi possível carregar suas plataformas agora.
        </p>
      )}

      {platforms && platforms.length === 0 && (
        <div className="border-(--color-border) bg-(--color-bg-primary) rounded-xl border p-6">
          <p className="text-(--color-text-primary) text-sm font-medium">
            Nenhuma outra plataforma por aqui.
          </p>
          <p className="text-(--color-text-secondary) mt-1 text-sm">
            Quando você tiver acesso a outra plataforma, ela aparece nesta
            lista.
          </p>
        </div>
      )}

      {platforms && platforms.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          {platforms.map((platform) => (
            <PlatformCard
              key={platform.platformProfileId}
              platform={platform}
              isPending={pendingScopeId === platform.scopeId}
              onGo={() => void goToPlatform(platform)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface PlatformCardProps {
  platform: MyPlatform;
  isPending: boolean;
  onGo: () => void;
}

function PlatformCard({ platform, isPending, onGo }: PlatformCardProps) {
  return (
    <div className="border-(--color-border) bg-(--color-bg-primary) flex flex-col rounded-xl border p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-(--color-text-primary) text-base font-semibold">
            {platform.platformName}
          </h2>
          <p className="text-(--color-text-secondary) mt-0.5 text-sm">
            {platform.name}
          </p>
        </div>
        {!platform.isActive && (
          <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-950/50 dark:text-amber-400">
            Desativado
          </span>
        )}
      </div>

      {/*
        Perfil inativo aparece — esconder faria a pessoa achar que perdeu o
        acesso por bug e abrir chamado. Mas sem botão: com botão, ela atravessa
        um redirect só para receber um erro do outro lado.
      */}
      {!platform.isActive && (
        <p className="text-(--color-text-secondary) mt-3 text-sm">
          Seu acesso a esta plataforma está desativado. Fale com o suporte para
          reativá-lo.
        </p>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onGo}
          disabled={!platform.isActive || isPending}
          className="bg-(--color-accent) rounded-lg px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isPending ? 'Abrindo…' : 'Ir para plataforma'}
        </button>
        <Link
          to={`/platforms/${platform.platformProfileId}/perks`}
          className="border-(--color-border) text-(--color-text-primary) hover:bg-(--color-bg-secondary) rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
        >
          Ver meus acessos
        </Link>
      </div>
    </div>
  );
}

function PlatformsSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2" aria-hidden>
      {[0, 1].map((i) => (
        <div
          key={i}
          className="border-(--color-border) bg-(--color-bg-primary) h-44 animate-pulse rounded-xl border"
        />
      ))}
    </div>
  );
}
